/** SUNTRONIX ONE — ONE Follow-Up shared components + page. */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Phone, Mail, StickyNote, ArrowRight, PauseCircle, PlayCircle,
  StopCircle, Clock3, CalendarClock, RefreshCw, Plus,
  AlertTriangle, CheckCircle2, XCircle, ChevronDown,
} from "lucide-react";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);

const TONE = {
  OVERDUE:  "bg-rose-50 text-rose-800 border-rose-200",
  DUE_SOON: "bg-amber-50 text-amber-800 border-amber-200",
  SCHEDULED:"bg-slate-50 text-slate-700 border-slate-200",
};

/* ================================================================
 * Full page (route: /commercial/followup)
 * ================================================================ */
export default function FollowUpPage() {
  const [tab, setTab] = useState("today");
  return (
    <div className="max-w-[1500px] mx-auto space-y-4" data-testid="followup-page">
      <header className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-emerald-800 text-white grid place-items-center">
          <CalendarClock className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xl font-heading font-semibold text-slate-900">
            ONE Follow-Up
          </div>
          <div className="text-[11px] text-slate-500">
            Commercial follow-ups · Sales Desk view · Management view · Templates
          </div>
        </div>
      </header>
      <div className="border-b border-slate-200 flex flex-wrap gap-1">
        {[["today", "Follow Up Today"], ["management", "Management View"],
          ["templates", "Templates"]].map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)}
              data-testid={`followup-tab-${k}`}
              className={"px-3 py-1.5 text-[12px] -mb-px border-b-2 " +
                              (tab === k ? "border-emerald-800 text-emerald-900 font-medium"
                                                        : "border-transparent text-slate-500 hover:text-slate-900")}>
            {v}
          </button>
        ))}
      </div>
      {tab === "today"      && <SalesDeskFollowUpWidget />}
      {tab === "management" && <ManagementView />}
      {tab === "templates"  && <TemplatesTab />}
    </div>
  );
}


/* ================================================================
 * FOLLOW UP TODAY — embeddable in Sales Desk
 * ================================================================ */
export function SalesDeskFollowUpWidget() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/followup/sales-desk/today");
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="followup-today">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-slate-600">
          Prioritised open commercial follow-ups
        </div>
        <button onClick={load} disabled={busy}
            className="text-[11px] border border-slate-200 rounded-md px-2.5 py-1 inline-flex items-center gap-1 hover:bg-slate-50"
            data-testid="followup-refresh">
          <RefreshCw className={"h-3 w-3 " + (busy ? "animate-spin" : "")} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Overdue"    value={data?.counts?.overdue ?? "—"} tone="err" />
        <Stat label="Due soon"   value={data?.counts?.due_soon ?? "—"} tone="warn" />
        <Stat label="Scheduled" value={data?.counts?.scheduled ?? "—"} />
        <Stat label="Total"      value={data?.counts?.total ?? "—"} tone="ok" />
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <table className="min-w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
            <tr>
              <th className="p-2 text-left">Priority</th>
              <th className="p-2 text-left">Quote</th>
              <th className="p-2 text-right">Value</th>
              <th className="p-2 text-left">Contact</th>
              <th className="p-2 text-left">Next</th>
              <th className="p-2 text-left">Due</th>
              <th className="p-2 text-left">Age</th>
              <th className="p-2 text-left">Response</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items || []).map((r) => (
              <tr key={r.plan_id} className="border-t border-slate-100"
                  data-testid={`followup-row-${r.plan_id}`}>
                <td className="p-2 font-mono">{r.priority_score}</td>
                <td className="p-2">
                  <Link to={`/commercial/crm/quotations?open=${r.quotation_id}`}
                    className="text-emerald-800 hover:underline font-semibold"
                    data-testid={`followup-open-quote-${r.plan_id}`}>
                    {r.quote_code || r.quotation_id.slice(0, 8)}
                  </Link>
                </td>
                <td className="p-2 text-right font-mono">{fmtINR(r.quote_value_inr)}</td>
                <td className="p-2">{r.customer_contact_name || r.customer_email || "—"}</td>
                <td className="p-2">{r.next_action || "—"}</td>
                <td className="p-2">
                  <span className={"text-[10px] px-1.5 py-0.5 rounded border " + TONE[r.bucket]}>
                    {r.bucket}
                  </span>
                  <div className="text-[10px] text-slate-500 mt-0.5">{r.next_due_at?.slice(0, 10)}</div>
                </td>
                <td className="p-2 text-slate-500">{r.age_days}d</td>
                <td className="p-2 text-slate-600">{(r.response || "—").replace(/_/g, " ")}</td>
                <td className="p-2 space-x-1">
                  <FollowUpQuickActions planId={r.plan_id} onChanged={load} />
                </td>
              </tr>
            ))}
            {!data?.items?.length && !busy && (
              <tr><td colSpan={9} className="p-6 text-center text-slate-400">
                Nothing to follow up. Start a follow-up plan from any issued quote.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function FollowUpQuickActions({ planId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [respOpen, setRespOpen] = useState(false);
  const [snoozeDays, setSnoozeDays] = useState(3);

  const snooze = async () => {
    const reason = window.prompt(`Snooze for ${snoozeDays} days — reason?`);
    if (!reason) return;
    setBusy(true);
    try {
      await api.post(`/followup/plans/${planId}/snooze`, { days: snoozeDays, reason });
      toast.success("Snoozed");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Snooze failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="inline-flex items-center gap-1">
      <button onClick={snooze} disabled={busy} title="Snooze"
        data-testid={`followup-snooze-${planId}`}
        className="text-[10px] px-1.5 py-1 rounded border border-slate-200 hover:bg-slate-50">
        Snooze
      </button>
      <button onClick={() => setRespOpen(true)}
        data-testid={`followup-respond-${planId}`}
        className="text-[10px] px-1.5 py-1 rounded border border-emerald-200 text-emerald-800 hover:bg-emerald-50">
        Response
      </button>
      {respOpen && <ResponseModal planId={planId}
          onClose={() => setRespOpen(false)}
          onSaved={() => { setRespOpen(false); onChanged?.(); }} />}
    </div>
  );
}


function ResponseModal({ planId, onClose, onSaved }) {
  const [meta, setMeta] = useState(null);
  const [response, setResponse] = useState("CUSTOMER_INTERESTED");
  const [expected, setExpected] = useState("");
  const [next, setNext] = useState("");
  const [notes, setNotes] = useState("");
  const [stopNow, setStopNow] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/followup/meta").then(({ data }) => setMeta(data)); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/followup/plans/${planId}/response`, {
        response, expected_decision_date: expected || null,
        next_follow_up_date: next || null, notes, stop_now: stopNow,
      });
      toast.success("Response recorded");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4"
        data-testid="followup-response-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-heading font-semibold">Record customer response</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <XCircle className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 space-y-3 text-[12px]">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Response</div>
            <select value={response} onChange={(e) => setResponse(e.target.value)}
                data-testid="followup-response-select"
                className="w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
              {(meta?.response_states || []).map((r) =>
                <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Expected decision
              <input type="date" value={expected} onChange={(e) => setExpected(e.target.value)}
                  data-testid="followup-expected"
                  className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5" />
            </label>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Next follow-up
              <input type="date" value={next} onChange={(e) => setNext(e.target.value)}
                  data-testid="followup-next"
                  className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5" />
            </label>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Notes</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                rows={3} data-testid="followup-notes"
                className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          </div>
          <label className="flex items-center gap-2 text-[11px] text-slate-700">
            <input type="checkbox" checked={stopNow} onChange={(e) => setStopNow(e.target.checked)}
                data-testid="followup-stop-now" />
            Stop follow-up now (customer response ends this sequence)
          </label>
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
          <button onClick={submit} disabled={busy}
              data-testid="followup-submit"
              className="text-[12px] px-3 py-1.5 rounded-md bg-emerald-800 text-white disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ================================================================
 * QUOTE 360 · Follow-Up panel (embed in QuoteDrawer)
 * ================================================================ */
export function QuoteFollowUpPanel({ quotationId, onChanged }) {
  const [plan, setPlan] = useState(null);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/followup/plans/by-quote/${quotationId}`);
      setPlan(data.plan); setEvents(data.events || []);
    } catch { /* silent */ }
  }, [quotationId]);
  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/followup/plans/start", { quotation_id: quotationId });
      toast.success("Follow-up plan started");
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Start failed"); }
    finally { setBusy(false); }
  };

  const pauseOrResume = async () => {
    if (!plan) return;
    setBusy(true);
    try {
      if (plan.state === "ACTIVE") {
        const reason = window.prompt("Pause reason?");
        if (!reason) { setBusy(false); return; }
        await api.post(`/followup/plans/${plan.id}/pause`, { reason });
        toast.success("Paused");
      } else {
        await api.post(`/followup/plans/${plan.id}/resume`);
        toast.success("Resumed");
      }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const stop = async () => {
    if (!plan) return;
    const reason = window.prompt("Reason to stop follow-up?");
    if (!reason) return;
    setBusy(true);
    try {
      await api.post(`/followup/plans/${plan.id}/stop`,
        { reason, event: "MANUAL_STOP" });
      toast.success("Stopped");
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid="quote-followup-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-semibold text-slate-700 flex items-center gap-1">
          <CalendarClock className="h-3.5 w-3.5 text-emerald-800" /> Follow-Up
        </div>
        {!plan && (
          <button onClick={start} disabled={busy}
              data-testid="quote-followup-start"
              className="text-[11px] px-2.5 py-1 rounded-md bg-emerald-800 text-white">
            {busy ? "Starting…" : "Start follow-up plan"}
          </button>
        )}
      </div>
      {!plan ? (
        <div className="text-[11px] text-slate-400">No follow-up plan yet.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            <MiniStat k="State" v={plan.state} tone={plan.state === "ACTIVE" ? "ok"
                                                                        : plan.state === "PAUSED" ? "warn" : "muted"} />
            <MiniStat k="Template" v={plan.template_name} />
            <MiniStat k="Priority" v={plan.priority_score} />
            <MiniStat k="Owner" v={plan.owner_email} />
          </div>
          <div className="mt-2 space-y-1">
            {(plan.steps || []).map((s) => (
              <div key={s.order} className={"flex items-center gap-2 text-[11px] p-1.5 rounded " +
                  (s.state === "DONE" ? "bg-emerald-50" : "bg-slate-50")}
                  data-testid={`quote-followup-step-${s.order}`}>
                <span className="font-mono text-slate-500 w-6">#{s.order}</span>
                <span className="flex-1">{s.label}</span>
                <span className="text-slate-500 font-mono">{s.due_at?.slice(0, 10)}</span>
                <span className={"text-[9px] px-1.5 py-0.5 rounded ring-1 " +
                    (s.state === "DONE" ? "bg-emerald-100 text-emerald-800 ring-emerald-200"
                                                        : "bg-white text-slate-600 ring-slate-200")}>
                  {s.state}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button onClick={pauseOrResume} disabled={busy || plan.state === "COMPLETED" || plan.state === "STOPPED"}
                data-testid="quote-followup-pause-resume"
                className="text-[11px] px-2.5 py-1 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
              {plan.state === "ACTIVE" ? <><PauseCircle className="h-3 w-3" /> Pause</>
                                                          : <><PlayCircle className="h-3 w-3" /> Resume</>}
            </button>
            <button onClick={stop} disabled={busy || plan.state === "STOPPED"}
                data-testid="quote-followup-stop"
                className="text-[11px] px-2.5 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1">
              <StopCircle className="h-3 w-3" /> Stop
            </button>
            {events.length > 0 && (
              <details className="ml-auto text-[10px] text-slate-500">
                <summary className="cursor-pointer">History ({events.length})</summary>
                <div className="mt-2 space-y-1">
                  {events.map((e) => (
                    <div key={e.id} className="border-l-2 border-slate-200 pl-2">
                      <div className="font-semibold">{e.kind}</div>
                      <div>{e.detail}</div>
                      <div className="text-slate-400">{e.at?.slice(0, 19)} · {e.actor}</div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </>
      )}
    </div>
  );
}


/* ================================================================
 * CUSTOMER 360 · open commercial follow-ups
 * ================================================================ */
export function CustomerOpenFollowUps({ customerId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/followup/customer/${customerId}/open`)
        .then(({ data }) => setRows(data.items || []))
        .catch(() => { /* silent */ });
  }, [customerId]);
  if (!rows.length) return null;
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid="cust-followup-open">
      <div className="text-[11px] font-semibold text-slate-700 mb-2 flex items-center gap-1">
        <CalendarClock className="h-3.5 w-3.5 text-emerald-800" /> Open commercial follow-ups
      </div>
      <div className="space-y-1 text-[11px]">
        {rows.map((r) => (
          <Link key={r.id} to={`/commercial/crm/quotations?open=${r.quotation_id}`}
              className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50">
            <span className="font-mono">{r.quote_code}</span>
            <span className="text-slate-500 flex-1">{r.template_name}</span>
            <span className={"text-[9px] px-1.5 py-0.5 rounded ring-1 " +
                (r.state === "ACTIVE" ? "bg-emerald-100 text-emerald-800 ring-emerald-200"
                                                        : "bg-amber-100 text-amber-800 ring-amber-200")}>
              {r.state}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}


/* ================================================================
 * MANAGEMENT VIEW
 * ================================================================ */
function ManagementView() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/followup/management/overview")
        .then(({ data }) => setData(data))
        .catch((e) => toast.error(e.response?.data?.detail || "Load failed"));
  }, []);
  if (!data) return <div className="text-[12px] text-slate-500">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="followup-mgmt">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <Stat label="Open quotations in follow-up"
              value={data.open_quotations_in_followup} tone="ok" />
        <Stat label="Awaiting customer response"
              value={data.awaiting_response} tone="warn" />
        <Stat label="Overdue follow-ups"
              value={data.overdue_followups} tone="err" />
        <Stat label="PO expected"           value={data.po_expected} tone="ok" />
        <Stat label="Revision requested" value={data.revision_requested} tone="warn" />
        <Stat label="No activity 7+ days" value={data.no_recent_activity_7d} tone="err" />
      </div>
      <div className="text-[10px] text-slate-400">{data.note}</div>
    </div>
  );
}


/* ================================================================
 * TEMPLATES (Configuration Studio → Commercial → Follow-Up)
 * ================================================================ */
function TemplatesTab() {
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/followup/templates");
      setRows(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, []);
  useEffect(() => { load(); api.get("/followup/meta").then(({ data }) => setMeta(data)); }, [load]);

  return (
    <div className="space-y-3" data-testid="followup-templates">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-slate-600">
          Follow-up plans reused by every issued quote. Configuration Studio → Commercial → Follow-Up.
        </div>
        {meta?.current_user?.can_admin && (
          <button onClick={() => setShowNew(true)}
              data-testid="tpl-new"
              className="text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white inline-flex items-center gap-1">
            <Plus className="h-3 w-3" /> New template
          </button>
        )}
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <table className="min-w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
            <tr>
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Steps</th>
              <th className="p-2 text-left">Stop events</th>
              <th className="p-2 text-left">Default?</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`tpl-row-${t.id}`}>
                <td className="p-2 font-semibold">{t.name}</td>
                <td className="p-2 text-slate-600">
                  {(t.steps || []).map((s) =>
                    `D+${s.day_offset} ${s.mode}`).join(" · ") || "—"}
                </td>
                <td className="p-2 text-[10px] font-mono">{(t.stop_events || []).join(", ")}</td>
                <td className="p-2">{t.default ? "★ default" : "—"}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={4} className="p-6 text-center text-slate-400">
                No templates yet. Create one to enable quotation follow-up.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {showNew && <TemplateFormModal onClose={() => setShowNew(false)}
                                                                  onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}


function TemplateFormModal({ onClose, onCreated }) {
  const [name, setName] = useState("Standard Quotation");
  const [desc, setDesc] = useState("Internal reminders every 2 / 4 / 7 / 10 days.");
  const [steps, setSteps] = useState([
    { order: 1, day_offset: 2, mode: "INTERNAL_REMINDER", label: "First internal reminder" },
    { order: 2, day_offset: 4, mode: "CREATE_FOLLOWUP_TASK", label: "Follow-Up due — call/email customer" },
    { order: 3, day_offset: 7, mode: "INTERNAL_REMINDER", label: "Second follow-up reminder" },
    { order: 4, day_offset: 10, mode: "INTERNAL_REMINDER", label: "Management visibility" },
  ]);
  const [asDefault, setAsDefault] = useState(true);
  const [busy, setBusy] = useState(false);

  const setStep = (i, patch) => setSteps((s) => s.map((x, ix) => ix === i ? { ...x, ...patch } : x));
  const addStep = () => setSteps((s) => [...s, { order: s.length + 1, day_offset: (s[s.length - 1]?.day_offset || 0) + 3,
                                                                                                mode: "INTERNAL_REMINDER", label: "Reminder" }]);
  const rmStep = (i) => setSteps((s) => s.filter((_, ix) => ix !== i).map((x, ix) => ({ ...x, order: ix + 1 })));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/followup/templates",
        { name, description: desc, steps, default: asDefault });
      toast.success("Template created");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4"
        data-testid="tpl-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-heading font-semibold">New follow-up template</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <XCircle className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 space-y-3 text-[12px]">
          <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Template name" data-testid="tpl-name"
              className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)}
              rows={2} placeholder="Description"
              className="w-full border border-slate-200 rounded-md px-2 py-1.5" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Steps</div>
            {steps.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center border border-slate-200 rounded-md p-2 mb-1"
                  data-testid={`tpl-step-${i}`}>
                <span className="col-span-1 font-mono">#{s.order}</span>
                <input type="number" value={s.day_offset}
                    onChange={(e) => setStep(i, { day_offset: parseInt(e.target.value) || 0 })}
                    className="col-span-2 border border-slate-200 rounded-md px-2 py-1 text-right" />
                <select value={s.mode} onChange={(e) => setStep(i, { mode: e.target.value })}
                    className="col-span-3 border border-slate-200 rounded-md px-2 py-1">
                  {["INTERNAL_REMINDER", "CREATE_FOLLOWUP_TASK",
                    "PREPARE_EMAIL_DRAFT", "SEND_APPROVED_EMAIL",
                    "MANUAL_CUSTOMER_CALL", "CUSTOMER_RESPONSE_REVIEW"].map((m) =>
                      <option key={m} value={m}>{m}</option>)}
                </select>
                <input value={s.label} onChange={(e) => setStep(i, { label: e.target.value })}
                    className="col-span-5 border border-slate-200 rounded-md px-2 py-1" />
                <button onClick={() => rmStep(i)}
                    className="col-span-1 text-rose-600 hover:text-rose-800">×</button>
              </div>
            ))}
            <button onClick={addStep}
                className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
              <Plus className="h-3 w-3" /> Add step
            </button>
          </div>
          <label className="flex items-center gap-2 text-[11px]">
            <input type="checkbox" checked={asDefault} onChange={(e) => setAsDefault(e.target.checked)}
                data-testid="tpl-default" />
            Make this the default template for new plans
          </label>
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
          <button onClick={submit} disabled={busy}
              data-testid="tpl-submit"
              className="text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
            {busy ? "Saving…" : "Create template"}
          </button>
        </div>
      </div>
    </div>
  );
}


function MiniStat({ k, v, tone }) {
  const tint = tone === "ok" ? "text-emerald-800 bg-emerald-50"
             : tone === "warn" ? "text-amber-800 bg-amber-50"
             : tone === "err" ? "text-rose-800 bg-rose-50"
             : tone === "muted" ? "text-slate-600 bg-slate-100"
             : "text-slate-700 bg-white";
  return (
    <div className={"rounded p-1.5 " + tint}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{k}</div>
      <div className="text-[12px] font-semibold truncate">{v ?? "—"}</div>
    </div>
  );
}


function Stat({ label, value, tone }) {
  const tint = tone === "ok" ? "text-emerald-800 bg-emerald-50 border-emerald-200"
             : tone === "warn" ? "text-amber-800 bg-amber-50 border-amber-200"
             : tone === "err" ? "text-rose-800 bg-rose-50 border-rose-200"
             : "text-slate-700 bg-white border-slate-200";
  return (
    <div className={"border rounded p-2.5 " + tint}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-[16px] font-heading font-semibold mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
